import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import {
  drawCard,
  drawSceneBackground,
  drawSceneTitle,
  fillRoundedRect,
  strokeRoundedRect,
} from "../../utils/draw";
import type { Battle } from "../systems/Battles";
import type { Button } from "../systems/Buttons";
import { backButton } from "../systems/Buttons";
import { MenuScene } from "./MenuScene";
import { RaceScene } from "./RaceScene";
import type { GameContext, Scene } from "./Scene";

const CARD_X = 20;
const CARD_W = 320;
const ROW_H = 52;
const ROW_GAP = 6;
const LIST_TOP = 150;
const LIST_BOTTOM = 600;
const LIST_H = LIST_BOTTOM - LIST_TOP;

type ActionDef = { label: string; color: string; run: () => void };

export class BattlesScene implements Scene {
  private scrollY = 0;
  private maxScroll = 0;
  private dragLastY: number | null = null;
  // Guards the async reload() below from re-stamping buttons after the scene left.
  private entered = false;

  constructor(private readonly ctxMgr: GameContext) {}

  enter(): void {
    this.entered = true;
    void this.reload();
    const canvas = this.ctxMgr.renderer.canvas;
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("pointerdown", this.onDragStart);
    window.addEventListener("pointermove", this.onDragMove);
    window.addEventListener("pointerup", this.onDragEnd);
    this.rebuild();
  }

  exit(): void {
    this.entered = false;
    this.ctxMgr.buttons.clear();
    const canvas = this.ctxMgr.renderer.canvas;
    canvas.removeEventListener("wheel", this.onWheel);
    canvas.removeEventListener("pointerdown", this.onDragStart);
    window.removeEventListener("pointermove", this.onDragMove);
    window.removeEventListener("pointerup", this.onDragEnd);
  }

  update(_dt: number): void {
    // static
  }

  private async reload(): Promise<void> {
    await this.ctxMgr.battles.refresh(
      this.ctxMgr.settings.playerName,
      this.ctxMgr.settings.authToken,
    );
    if (!this.entered) return;
    void this.ctxMgr.shop.refresh();
    this.rebuild();
  }

  private get myName(): string {
    return this.ctxMgr.settings.playerName;
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    drawSceneBackground(ctx);

    drawSceneTitle(ctx, "BATTLES");
    ctx.font = "11px system-ui, sans-serif";
    ctx.fillText("bet flies · higher score wins the pot", VIRTUAL_WIDTH / 2, 58);

    // wallet
    ctx.fillStyle = "#f0e0a8";
    ctx.font = "bold 13px system-ui, sans-serif";
    ctx.fillText(`Your flies: ${this.ctxMgr.shop.wallet}`, VIRTUAL_WIDTH / 2, 124);

    if (!this.ctxMgr.settings.isLoggedIn()) {
      ctx.fillStyle = "#f5e9c8";
      ctx.font = "13px system-ui, sans-serif";
      ctx.fillText("Log in to battle other players.", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT / 2);
      return;
    }

    const battles = this.ctxMgr.battles.all();
    if (battles.length === 0) {
      ctx.fillStyle = "#f5e9c8";
      ctx.font = "13px system-ui, sans-serif";
      ctx.fillText("No battles yet. Challenge someone!", VIRTUAL_WIDTH / 2, LIST_TOP + 40);
      return;
    }

    ctx.save();
    ctx.beginPath();
    ctx.rect(CARD_X - 2, LIST_TOP, CARD_W + 4, LIST_H);
    ctx.clip();

    const stride = ROW_H + ROW_GAP;
    for (let i = 0; i < battles.length; i += 1) {
      const rowY = LIST_TOP + i * stride - this.scrollY;
      if (rowY + ROW_H < LIST_TOP || rowY > LIST_TOP + LIST_H) continue;
      this.drawRow(ctx, battles[i]!, rowY);
    }
    ctx.restore();

    if (this.maxScroll > 0) {
      const trackX = CARD_X + CARD_W + 4;
      const thumbH = Math.max(20, (LIST_H / (this.maxScroll + LIST_H)) * LIST_H);
      const t = this.scrollY / this.maxScroll;
      ctx.fillStyle = "rgba(245,233,200,0.25)";
      ctx.fillRect(trackX, LIST_TOP, 3, LIST_H);
      ctx.fillStyle = "rgba(245,233,200,0.85)";
      ctx.fillRect(trackX, LIST_TOP + t * (LIST_H - thumbH), 3, thumbH);
    }
  }

  private drawRow(ctx: CanvasRenderingContext2D, b: Battle, rowY: number): void {
    drawCard(ctx, CARD_X, rowY, CARD_W, ROW_H, 10);

    const me = this.myName.toLowerCase();
    const other = b.challenger.toLowerCase() === me ? b.opponent : b.challenger;

    ctx.fillStyle = "#1a1f2b";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = "bold 14px system-ui, sans-serif";
    ctx.fillText(`vs ${other}`, CARD_X + 12, rowY + 16);
    ctx.font = "12px system-ui, sans-serif";
    ctx.fillStyle = "#7a5e2a";
    ctx.fillText(`${b.bet} flies · ${this.statusText(b)}`, CARD_X + 12, rowY + 36);
    ctx.textBaseline = "alphabetic";

    const actions = this.rowActions(b);
    actions.forEach((a, i) => {
      const r = actionRect(rowY, actions.length, i);
      drawActionButton(ctx, a.label, a.color, r);
    });
  }

  private statusText(b: Battle): string {
    const me = this.myName.toLowerCase();
    const iAmChallenger = b.challenger.toLowerCase() === me;
    if (b.status === "pending") {
      return iAmChallenger ? "waiting for accept" : "challenged you!";
    }
    if (b.status === "active") {
      const myScore = iAmChallenger ? b.challengerScore : b.opponentScore;
      return myScore === null ? "ready — race now!" : "waiting for opponent";
    }
    if (b.status === "settled") {
      if (b.winner === "tie") return "tie — bet refunded";
      const iWon = (b.winner ?? "").toLowerCase() === me;
      return iWon ? `you won +${b.bet * 2} flies` : "you lost";
    }
    return b.status === "declined" ? "declined" : "canceled";
  }

  private rowActions(b: Battle): ActionDef[] {
    const me = this.myName.toLowerCase();
    const iAmChallenger = b.challenger.toLowerCase() === me;
    if (b.status === "pending") {
      if (iAmChallenger) {
        return [{ label: "Cancel", color: "#8e1a1a", run: () => void this.doCancel(b) }];
      }
      return [
        { label: "Accept", color: "#2e7d32", run: () => void this.doAccept(b) },
        { label: "Decline", color: "#8e1a1a", run: () => void this.doCancel(b) },
      ];
    }
    if (b.status === "active") {
      const myScore = iAmChallenger ? b.challengerScore : b.opponentScore;
      if (myScore === null) {
        return [{ label: "Race", color: "#2e7d32", run: () => this.doRace(b) }];
      }
    }
    return [];
  }

  private async doCreate(): Promise<void> {
    if (!this.ctxMgr.settings.isLoggedIn()) {
      window.alert("Log in to start a battle.");
      return;
    }
    const opp = (window.prompt("Challenge which player? (exact name)", "") ?? "").trim();
    if (!opp) return;
    const raw = window.prompt(`Bet how many flies? (you have ${this.ctxMgr.shop.wallet})`, "100");
    if (raw === null) return;
    const bet = Math.floor(Number(raw));
    if (!Number.isFinite(bet) || bet <= 0) {
      window.alert("Enter a positive number of flies to bet.");
      return;
    }
    if (bet > this.ctxMgr.shop.wallet) {
      window.alert("You don't have that many flies.");
      return;
    }
    const res = await this.ctxMgr.battles.create(
      this.myName,
      this.ctxMgr.settings.authToken,
      opp,
      bet,
    );
    if (!res.ok) {
      window.alert(createError(res.error, opp));
      return;
    }
    await this.reload();
  }

  private async doAccept(b: Battle): Promise<void> {
    if (b.bet > this.ctxMgr.shop.wallet) {
      window.alert(`You need ${b.bet} flies to accept this battle.`);
      return;
    }
    const res = await this.ctxMgr.battles.accept(this.myName, this.ctxMgr.settings.authToken, b.id);
    if (!res.ok) {
      window.alert(res.error === "insufficient_funds" ? "Not enough flies." : "Couldn't accept.");
    }
    await this.reload();
  }

  private async doCancel(b: Battle): Promise<void> {
    const iAmChallenger = b.challenger.toLowerCase() === this.myName.toLowerCase();
    const verb = iAmChallenger ? "Cancel" : "Decline";
    if (!window.confirm(`${verb} this battle vs "${iAmChallenger ? b.opponent : b.challenger}"?`))
      return;
    await this.ctxMgr.battles.cancel(this.myName, this.ctxMgr.settings.authToken, b.id);
    await this.reload();
  }

  private doRace(b: Battle): void {
    const me = this.myName.toLowerCase();
    const opponent = b.challenger.toLowerCase() === me ? b.opponent : b.challenger;
    // Playing a battle = a live ghost race; the result settles the pot.
    this.ctxMgr.goTo(new RaceScene(this.ctxMgr, { id: b.id, bet: b.bet, opponent }));
  }

  private rebuild(): void {
    const buttons: Button[] = [];

    buttons.push(backButton(() => this.ctxMgr.goTo(new MenuScene(this.ctxMgr))));

    if (this.ctxMgr.settings.isLoggedIn()) {
      const nb = { x: CARD_X, y: 74, w: CARD_W, h: 34 };
      buttons.push({
        ...nb,
        draw: (ctx, hit) => drawWideButton(ctx, "+ New Battle", nb, hit),
        onPress: () => void this.doCreate(),
      });
    }

    const battles = this.ctxMgr.battles.all();
    const stride = ROW_H + ROW_GAP;
    this.maxScroll = Math.max(0, battles.length * stride - ROW_GAP - LIST_H);
    if (this.scrollY > this.maxScroll) this.scrollY = this.maxScroll;

    battles.forEach((b, i) => {
      const rowY = LIST_TOP + i * stride - this.scrollY;
      const actions = this.rowActions(b);
      actions.forEach((a, ai) => {
        const r = actionRect(rowY, actions.length, ai);
        if (r.y < LIST_TOP || r.y + r.h > LIST_TOP + LIST_H) return;
        buttons.push({ ...r, draw: () => undefined, onPress: a.run });
      });
    });

    this.ctxMgr.buttons.set(buttons);
  }

  private readonly onWheel = (e: WheelEvent): void => {
    if (this.maxScroll <= 0) return;
    e.preventDefault();
    this.scrollY = clamp(this.scrollY + e.deltaY, 0, this.maxScroll);
    this.rebuild();
  };

  private readonly onDragStart = (e: PointerEvent): void => {
    if (this.maxScroll <= 0) return;
    const { x, y } = this.ctxMgr.renderer.clientToCanvas(e.clientX, e.clientY);
    if (x >= CARD_X && x <= CARD_X + CARD_W && y >= LIST_TOP && y <= LIST_TOP + LIST_H) {
      this.dragLastY = e.clientY;
    }
  };

  private readonly onDragMove = (e: PointerEvent): void => {
    if (this.dragLastY === null) return;
    const dyClient = e.clientY - this.dragLastY;
    this.dragLastY = e.clientY;
    const rect = this.ctxMgr.renderer.canvas.getBoundingClientRect();
    const dy = (dyClient / rect.height) * VIRTUAL_HEIGHT;
    this.scrollY = clamp(this.scrollY - dy, 0, this.maxScroll);
    this.rebuild();
  };

  private readonly onDragEnd = (): void => {
    this.dragLastY = null;
  };
}

function createError(error: string | undefined, opp: string): string {
  switch (error) {
    case "opponent_not_found":
      return `No player named "${opp}".`;
    case "self_battle":
      return "You can't battle yourself.";
    case "insufficient_funds":
      return "You don't have enough flies for that bet.";
    case "invalid_bet":
      return "Enter a positive bet.";
    case "session_expired":
    case "unauthorized":
      return "Your session expired. Log in again.";
    default:
      return "Couldn't create the battle.";
  }
}

function actionRect(
  rowY: number,
  count: number,
  index: number,
): { x: number; y: number; w: number; h: number } {
  const w = 66;
  const x = CARD_X + CARD_W - w - 10;
  if (count <= 1) return { x, y: rowY + (ROW_H - 26) / 2, w, h: 26 };
  const h = 20;
  const y = rowY + 6 + index * (h + 4);
  return { x, y, w, h };
}

function drawActionButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  color: string,
  r: { x: number; y: number; w: number; h: number },
): void {
  ctx.fillStyle = color;
  fillRoundedRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.fillStyle = "#fff";
  ctx.font = "bold 12px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2);
  ctx.textBaseline = "alphabetic";
}

function drawWideButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  r: { x: number; y: number; w: number; h: number },
  hit: boolean,
): void {
  ctx.fillStyle = hit ? "#7a5e2a" : "#f5e9c8";
  fillRoundedRect(ctx, r.x, r.y, r.w, r.h, 8);
  ctx.strokeStyle = "#7a5e2a";
  ctx.lineWidth = 2;
  strokeRoundedRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 8);
  ctx.fillStyle = hit ? "#f5e9c8" : "#7a5e2a";
  ctx.font = "bold 15px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2);
  ctx.textBaseline = "alphabetic";
}


function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
